import React, { ReactNode } from 'react'
import { Table as DsTable } from '@systemeio/components'
import EmptyList from './empty-list'
import TableLimitPagination, {
  DataWithPaginationInterface,
  PaginationInterface,
} from './table-limit-pagination'

export interface ColumnDefinitionInterface<T> {
  key: keyof T | T[keyof T]
  header: string | JSX.Element
  widthPercent?: number
  onRender?: (instance: T) => JSX.Element
  onRenderSmall?: (instance: T) => JSX.Element
  actionsCount?: number
}

export interface TableProps<T> {
  data: DataWithPaginationInterface<T> | undefined
  columns: ColumnDefinitionInterface<T>[]
  limiter?: {
    limit: number
    setLimit: React.Dispatch<React.SetStateAction<number>>
  }
  paginator?: {
    pagination: PaginationInterface
    setPagination: React.Dispatch<React.SetStateAction<PaginationInterface>>
    hasMore?: boolean
  }
  testAttributePostfix?: string
  className?: string
}

const resolveContent = <T,>(
  row: T,
  column: ColumnDefinitionInterface<T>,
): T[keyof T] | T | undefined => {
  const keys = (column.key as string).split('.')
  let content: T[keyof T] | T | undefined = undefined
  keys.forEach(key => {
    content = content ? (content as T)[key as keyof T] : row[key as keyof T]
  })
  return content
}

const SKELETON_ROW_COUNT = 10

const skeletonItems = Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => ({
  id: `skeleton-row-${index}`,
}))

type SkeletonItem = (typeof skeletonItems)[number]
type ColumnItem<U> = ColumnDefinitionInterface<U> & {
  id: string
}

export function Table<T extends { id: number | string }>({
  data,
  columns,
  limiter,
  paginator,
  testAttributePostfix,
  className,
}: TableProps<T>): ReactNode {
  if (!data?.items?.length && data !== undefined) {
    return <EmptyList />
  }

  const columnItems: ColumnItem<T>[] = columns.map((column, index) => ({
    ...column,
    id: `column-${index}`,
  }))

  const bodyItems: Array<T | SkeletonItem> = data?.items ?? skeletonItems

  const ariaLabel =
    columns
      .map(column => (typeof column.header === 'string' ? column.header : ''))
      .filter(Boolean)
      .join(', ') ||
    testAttributePostfix?.replaceAll('-', ' ') ||
    'table'

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="hidden lg:block" data-test-table-is-loaded={!!data}>
        <DsTable.Root aria-label={ariaLabel} className={className}>
          <DsTable.Header>
            {columnItems.map(column => (
              <DsTable.Head
                key={column.id}
                id={column.id}
                style={{
                  width:
                    column.key === 'action'
                      ? column.actionsCount !== undefined
                        ? `${35 * column.actionsCount + 33}px`
                        : undefined
                      : `${column.widthPercent ?? 25}%`,
                }}
              >
                {column.header}
              </DsTable.Head>
            ))}
          </DsTable.Header>
          <DsTable.Body>
            {bodyItems.map(row => {
              if (data === undefined) {
                return (
                  <DsTable.Row key={row.id} id={row.id}>
                    {columnItems.map(column => (
                      <DsTable.LoaderCell key={column.id} />
                    ))}
                  </DsTable.Row>
                )
              }
              const item = row as T
              return (
                <DsTable.Row
                  key={item.id}
                  id={item.id}
                  {...(testAttributePostfix
                    ? {
                        [`data-test-${testAttributePostfix}`]: `${testAttributePostfix}-item-${item.id}`,
                      }
                    : {})}
                >
                  {columnItems.map(column => (
                    <DsTable.Cell key={column.id}>
                      {
                        (column.onRender
                          ? column.onRender(item)
                          : resolveContent(item, column)) as ReactNode
                      }
                    </DsTable.Cell>
                  ))}
                </DsTable.Row>
              )
            })}
          </DsTable.Body>
        </DsTable.Root>
      </div>
      <div className="lg:hidden" data-test-table-is-loaded={!!data}>
        <DsTable.Mobile.Root className={className} selectionMode="none" selectedKeys={undefined}>
          <DsTable.Mobile.Body>
            {data === undefined
              ? [...Array.from({ length: 10 }).keys()].map(index => (
                  <DsTable.Mobile.Card key={`row-${index}`}>
                    {columns.map((column, index2) => (
                      <DsTable.Mobile.LoaderCell
                        key={`cell-${index2}`}
                        label={column.onRenderSmall ? undefined : column.header}
                      />
                    ))}
                  </DsTable.Mobile.Card>
                ))
              : data.items.map(row => (
                  <DsTable.Mobile.Card
                    className="bg-bg-primary hover:bg-bg-primary"
                    key={`row-${row.id}`}
                    id={row.id}
                    {...(testAttributePostfix
                      ? {
                          [`data-test-${testAttributePostfix}`]: `${testAttributePostfix}-item-${row.id}`,
                        }
                      : {})}
                  >
                    {columns.map((column, index2) => {
                      const content = resolveContent(row, column)
                      const rendered = column.onRenderSmall
                        ? column.onRenderSmall(row)
                        : column.onRender
                          ? column.onRender(row)
                          : (content as ReactNode)
                      return (
                        <DsTable.Mobile.Cell
                          key={`cell-${index2}`}
                          label={column.onRenderSmall ? undefined : column.header}
                        >
                          {rendered}
                        </DsTable.Mobile.Cell>
                      )
                    })}
                  </DsTable.Mobile.Card>
                ))}
          </DsTable.Mobile.Body>
        </DsTable.Mobile.Root>
      </div>
      {paginator && (
        <div className="flex items-center justify-end gap-5 sm:gap-10">
          {limiter && <TableLimitPagination paginator={paginator} limiter={limiter} data={data} />}
        </div>
      )}
    </div>
  )
}
